/**
 * Teadete haldamise store
 */
import alt from '../alt';
import _ from 'underscore';
import update from 'immutability-helper';
import NotificationActions from './NotificationActions';
import CONFIG from '../../config';
import Log from '../../Log';

/**
 * @typedef {Object} Notification
 * @property {string} level Notification level, one of: debug, info, success, warning, error
 * @property {string} message Notification message
 * @property {number} id Notification ID (integer) - automatically created by the NotificationStore
 * @property {number} actionId Action ID (integer) if this error was based on action result
 * @property {Date} timestamp Notification creation timestamp - automatically created by the NotificationStore
 * @property {string} state Notification state, one of: "visible", "dismissed" - automatically created by the NotificationStore
 */
/**
 * @typedef {object} NotificationState
 * @property {Notification[]} data
 * @property {number} lastId
 */
/**
 * Notification store class
 */
class NotificationStore {
    constructor() {
        /**
         *
         * @type {NotificationState}
         */
        this.state = {
            data: [],
            lastId: 0
        };
        this.timeoutId = null;
        this.bindActions(NotificationActions);
        this.exportPublicMethods({
            dismissNotification: this.dismissNotification,
            removeNotification: this.removeNotification,
            removeOldNotifications: this.removeOldNotifications
        });
    }

    /**
     * @protected
     * @param level
     * @param message
     */
    storeNotification(level, message) {
        this.storeNotificationInfo({level: level, message: message});
    }

    /**
     * @protected
     * @param {Notification} info
     */
    storeNotificationInfo(info) {
        Log.debug('[' + info.level + '] ' + info.message);
        if (!info.actionId || _.find(this.state.data, function (item) {
                return item.actionId === info.actionId;
            }) === undefined) {
            info.id = this.state.lastId+1;
            info.timestamp = new Date();
            info.state = "visible";
            if (CONFIG.notificationMessageDismissTimeout) info.timeoutId = setTimeout(this.dismissNotification.bind(this, info.id), CONFIG.notificationMessageDismissTimeout*1000);
            // let notifications = this.state.data.slice();
            // notifications.push(info);
            this.setState({
                data: update(this.state.data, {$push: [info]}),
                lastId: info.id});

            if (!this.timeoutId) this.timeoutId = setTimeout(this.processRemoval.bind(this), CONFIG.notificationMessageTtl*1000);
        }
    }

    processRemoval() {
        this.removeOldNotifications();
        if (this.state.data.length > 0) this.timeoutId = setTimeout(this.processRemoval.bind(this), CONFIG.notificationMessageTtl*1000);
        else this.timeoutId = null;
    }

    /**
     * @public
     * @param id
     */
    dismissNotification(id) {
        let notificationIdx = _.findIndex(this.state.data, {id: id});
        if (notificationIdx >= 0) {
            // let notifications = this.state.data.slice();
            // notifications[notificationIdx].state = "dismissed";
            this.setState({data: update(this.state.data, {[notificationIdx]: {state: {$set: "dismissed"}}})});
        }
    }

    /**
     * @public
     */
    removeOldNotifications() {
        let timestamp = new Date(Date.now() - CONFIG.notificationMessageTtl*1000);
        let ttlTime = timestamp.getTime();
        let notifications = _.filter(this.state.data, function (item) {
            return item.timestamp.getTime() >= ttlTime;
        });
        if (notifications.length !== this.state.data.length) {
            this.setState({data: notifications});
            return true;
        }
        return false;
    }

    /**
     * @public
     * @param id
     */
    removeNotification(id) {
        let notificationIdx = _.findIndex(this.state.data, {id: id});
        if (notificationIdx >= 0) {
            // let notifications = this.state.data.slice();
            // notifications.splice(notificationIdx, 1);
            this.setState({data: update(this.state.data, {$splice: [[notificationIdx, 1]]})});
        }
    }

    /**
     * @protected
     * @param message
     */
    onInfo(message) {
        this.storeNotification('info', message);
    }

    /**
     * @protected
     * @param message
     */
    onWarning(message) {
        this.storeNotification('warning', message);
    }

    /**
     * @protected
     * @param message
     */
    onSuccess(message) {
        this.storeNotification('success', message);
    }

    /**
     * @protected
     * @param message
     */
    onError(message) {
        this.storeNotification('error', message);
    }

    /**
     * @protected
     * @param id
     */
    onDismiss(id) {
        this.dismissNotification(id);
    }

    onActionError(action) {
        this.storeNotificationInfo({level: "error", actionId: action.actionId, message: action.error.message});
    }
}

/**
 *
 * @type {AltJS.AltStore<NotificationState>|NotificationStore}
 */
let notificationStore = alt.createStore(NotificationStore, 'NotificationStore');
export default notificationStore;
